import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import logger from '../../utils/logger';
const HeroContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '760px',
  height: 'auto',
  background: '#0f172a', // slate-900
  overflow: 'visible',
  margin: 0,
  padding: 0,
  [theme.breakpoints.down('lg')]: {
    minHeight: '700px'
  },
  [theme.breakpoints.down('md')]: {
    minHeight: '600px'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '520px'
  }
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '82px',
  background: 'rgba(15, 23, 42, 0.8)', // slate-900 with transparency
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderBottom: '2px solid transparent',
  borderImage: 'linear-gradient(90deg, #22d3ee, #a5b4fc) 1',
  boxShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 86px',
  zIndex: 1000,
  [theme.breakpoints.down('md')]: {
    padding: '0 40px',
    height: '70px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 20px',
    height: '60px'
  }
}));

const LogoImage = styled(Box)(({ theme }) => ({
  width: '142px',
  height: '48px',
  backgroundImage: 'url(https://c.animaapp.com/mgbvruh0ta8bZ7/img/innovating-your-digital-future1-1-1.png)',
  backgroundSize: '100% 100%',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    width: '120px',
    height: '40px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100px',
    height: '34px'
  }
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '32px',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const NavLink = styled(Box)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '15px',
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8
  }
}));

const LoginButton = styled(Button)(({ theme }) => ({
  width: '130px',
  height: '48px',
  borderRadius: '27px',
  boxShadow: '0px 10px 30px rgba(212, 212, 212, 0.3)',
  background: 'linear-gradient(119deg, rgba(11, 145, 214, 1) 0%, rgba(239, 35, 42, 1) 100%)',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  color: '#ffffff',
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: '16px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(119deg, rgba(11, 145, 214, 0.9) 0%, rgba(239, 35, 42, 0.9) 100%)',
    boxShadow: '0px 12px 35px rgba(212, 212, 212, 0.4)'
  },
  [theme.breakpoints.down('md')]: {
    width: '110px',
    height: '40px',
    fontSize: '14px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '90px',
    height: '36px',
    fontSize: '12px'
  }
}));

const CircularBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '74px',
  left: 'calc(50% - 549px)',
  width: '1097px',
  height: '1097px',
  background: 'radial-gradient(140% 140% at 30% 30%, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0) 60%), radial-gradient(120% 120% at 70% 70%, rgba(236,72,153,0.08) 0%, rgba(236,72,153,0) 60%), linear-gradient(135deg, #f8fafc 0%, #ffffff 60%)',
  backgroundSize: 'cover',
  [theme.breakpoints.down('lg')]: {
    width: '800px',
    height: '800px',
    left: 'calc(50% - 400px)',
    top: '100px'
  },
  [theme.breakpoints.down('md')]: {
    width: '600px',
    height: '600px',
    left: 'calc(50% - 300px)'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const CircleBorder1 = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '190px',
  left: 'calc(50% - 409px)',
  width: '822px',
  height: '822px',
  borderRadius: '411px',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  [theme.breakpoints.down('lg')]: {
    width: '600px',
    height: '600px',
    borderRadius: '300px',
    left: 'calc(50% - 300px)',
    top: '150px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const CircleBorder2 = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '394px',
  left: 'calc(50% - 205px)',
  width: '413px',
  height: '413px',
  borderRadius: '206.5px',
  border: '1px solid rgba(148, 163, 184, 0.25)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const AIIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '284px',
  left: 'calc(50% - 223px)',
  width: '54px',
  height: '49px',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('xl')]: {
    left: 'calc(50% - 235px)',
    top: '290px'
  },
  [theme.breakpoints.down('lg')]: {
    left: 'calc(50% - 220px)',
    top: '285px',
    width: '45px',
    height: '42px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TopBadge = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '10px 16px',
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  borderRadius: '24px',
  border: '1px solid #334155', // slate-700
  zIndex: 2,
  alignSelf: 'flex-start',
  marginBottom: '16px'
}));

const MainHeadline = styled(Typography)(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 800,
  fontSize: '56px',
  color: '#ffffff',
  textAlign: 'left',
  letterSpacing: '-1.56px',
  lineHeight: '1.15',
  zIndex: 2,
  [theme.breakpoints.down('lg')]: {
    fontSize: '46px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '36px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px'
  }
}));

const Description = styled(Typography)(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  color: '#94a3b8', // slate-400
  textAlign: 'left',
  lineHeight: '29px',
  maxWidth: '720px',
  marginTop: '16px',
  zIndex: 2,
  [theme.breakpoints.down('lg')]: {
    fontSize: '16px',
    lineHeight: '26px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const GoogleIcon = styled('img')(({ theme }) => ({
  position: 'absolute',
  width: '42px',
  height: '42px',
  [theme.breakpoints.down('md')]: {
    width: '32px',
    height: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const SmallIconBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '30px',
  height: '30px',
  backgroundColor: 'rgba(11, 145, 214, 0.1)',
  borderRadius: '19px',
  overflow: 'hidden',
  border: '0.2px solid rgba(255, 255, 255, 0.1)',
  transform: 'rotate(-43.39deg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BlurEffect = styled(Box)(({ theme, top, left }) => ({
  position: 'absolute',
  top: top || '1006px',
  left: left || '1051px',
  width: '237px',
  height: '237px',
  borderRadius: '118.5px',
  filter: 'blur(141.1px)',
  background: 'rgba(59, 130, 246, 0.14)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const DashboardPreview = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 'calc(50% - 385px)',
  bottom: '1px',
  width: '770px',
  height: '620px',
  borderRadius: '12px',
  overflow: 'visible',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('lg')]: {
    width: '650px',
    height: '520px',
    left: 'calc(50% - 325px)'
  },
  [theme.breakpoints.down('md')]: {
    width: '90%',
    height: '450px',
    left: '5%'
  },
  [theme.breakpoints.down('sm')]: {
    width: '95%',
    height: '400px',
    left: '2.5%',
    bottom: '10px'
  }
}));

const CenterVideo = styled('video')(({ theme }) => ({
  position: 'absolute',
  left: 'calc(50% - 385px)',
  bottom: '0',
  width: '770px',
  height: '620px',
  border: 'none',
  outline: 'none',
  objectFit: 'contain',
  boxShadow: '0px 4px 20px rgba(11, 145, 214, 0.2)',
  borderRadius: '12px',
  [theme.breakpoints.down('lg')]: {
    width: '650px',
    height: '520px',
    left: 'calc(50% - 325px)',
    bottom: '0'
  },
  [theme.breakpoints.down('md')]: {
    width: '90%',
    height: '450px',
    left: '5%',
    bottom: '0'
  },
  [theme.breakpoints.down('sm')]: {
    width: '95%',
    height: '400px',
    left: '2.5%',
    bottom: '-50px',
    objectFit: 'contain'
  }
}));

const ProfileScoreCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '17px',
  left: '23px',
  width: '187px',
  height: '383px',
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  [theme.breakpoints.down('lg')]: {
    width: '160px',
    left: '10px'
  },
  [theme.breakpoints.down('md')]: {
    width: '140px',
    left: '5px'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const ProfileScoreInner = styled(Box)(({ theme }) => ({
  width: '187px',
  height: '153px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid rgba(11, 145, 214, 0.2)',
  boxShadow: '0px 1px 19.5px rgba(0, 0, 0, 0.1)',
  position: 'relative'
}));

const CompetitorListingsCard = styled(Box)(({ theme }) => ({
  width: '187px',
  height: '190px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid rgba(11, 145, 214, 0.2)',
  boxShadow: '0px 1px 19.5px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  padding: '12px 11px'
}));

const TaskListCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '159px',
  left: '556px',
  width: '187px',
  height: '216px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid rgba(11, 145, 214, 0.2)',
  boxShadow: '0px 1px 19.5px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  [theme.breakpoints.down('lg')]: {
    width: '160px',
    left: '460px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const AverageRatingCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '17px',
  left: '556px',
  width: '187px',
  height: '126px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid rgba(11, 145, 214, 0.2)',
  boxShadow: '0px 1px 19.5px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('lg')]: {
    width: '160px',
    left: '460px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const StatsCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '307px',
  left: '230px',
  width: '310px',
  height: '68px',
  backgroundColor: '#ffffff',
  borderRadius: '0 0 12px 12px',
  border: '1px solid #0B91D6',
  borderTop: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '0 22px',
  [theme.breakpoints.down('lg')]: {
    width: '260px',
    left: 'calc(50% - 130px)',
    top: '260px'
  },
  [theme.breakpoints.down('md')]: {
    width: '220px',
    left: 'calc(50% - 110px)',
    top: '210px',
    height: '60px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '180px',
    left: 'calc(50% - 90px)',
    top: '160px',
    height: '50px',
    padding: '0 10px',
    gap: '5px'
  }
}));

const TaskItem = styled(Box)(({ theme, completed }) => ({
  width: '159px',
  height: '33px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 12px',
  backgroundColor: completed ? '#34A853' : '#0B91D6',
  borderRadius: '4px',
  border: completed ? '0.2px solid rgba(160, 160, 170, 0.6)' : 'none'
}));

const GridContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '44px',
  left: '272px',
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gridTemplateRows: 'repeat(5, 1fr)',
  gap: '17px 20px',
  width: '225px',
  height: '236px',
  [theme.breakpoints.down('lg')]: {
    width: '180px',
    height: '190px',
    left: '220px',
    gap: '12px 15px'
  },
  [theme.breakpoints.down('md')]: {
    width: '140px',
    height: '150px',
    left: 'calc(50% - 70px)',
    top: '30px',
    gap: '8px 10px'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const GridPin = styled(Box)(({ theme }) => ({
  width: '29.18px',
  height: '33.83px',
  position: 'relative',
  overflow: 'hidden'
}));

const BuildingDesign = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: '-48px',
  left: 'calc(50% - 177px)',
  width: '355px',
  height: '97px',
  zIndex: 5,
  [theme.breakpoints.down('lg')]: {
    width: '300px',
    left: 'calc(50% - 150px)',
    bottom: '-40px'
  },
  [theme.breakpoints.down('md')]: {
    width: '250px',
    left: 'calc(50% - 125px)',
    bottom: '-35px'
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const SearchBar = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '56px',
  display: 'flex',
  gap: '10px',
  backgroundColor: '#1e293b', // slate-800
  borderRadius: '34px',
  border: '1px solid #334155', // slate-700
  boxShadow: '0 0 25px rgba(34, 211, 238, 0.15)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  alignItems: 'center',
  padding: '0 8px 0 20px',
  zIndex: 10,
  [theme.breakpoints.down('sm')]: {
    height: '48px'
  }
}));

const SearchInput = styled('input')(({ theme }) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  color: '#0f172a',
  textAlign: 'left',
  letterSpacing: '0',
  lineHeight: '20px',
  padding: '0 8px',
  margin: '0',
  width: '100%',
  boxShadow: 'none',
  '&:focus': {
    outline: 'none',
    border: 'none',
    boxShadow: 'none'
  },
  '&::placeholder': {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '15px',
    color: '#94a3b8',
    textAlign: 'left',
    letterSpacing: '0',
    lineHeight: '20px',
    opacity: 0.7
  },
  '&:focus::placeholder': {
    opacity: 0.5
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '18px',
    '&::placeholder': {
      fontSize: '14px',
      lineHeight: '18px'
    }
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    lineHeight: '10px',
    whiteSpace: 'normal',
    '&::placeholder': {
      fontSize: '10px',
      lineHeight: '10px'
    }
  }
}));

const ScanButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '42px',
  width: 'auto',
  minWidth: '120px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '12px 20px',
  borderRadius: '40px',
  boxShadow: '0px 13px 28px rgba(0, 0, 0, 0.1), 0px 202px 81px rgba(0, 0, 0, 0.01), 0px 316px 89px transparent',
  background: 'linear-gradient(136deg, rgba(11, 145, 214, 1) 20%, rgba(239, 35, 42, 1) 100%)',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)'
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '100px',
    height: '38px',
    padding: '10px 16px',
    gap: '6px'
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '90px',
    height: '36px',
    padding: '8px 12px',
    gap: '4px'
  }
}));

// New two-column hero layout (video left, content right)
const HeroLayout = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'grid',
  gridTemplateColumns: '1.05fr 1fr',
  gap: '40px',
  alignItems: 'center',
  width: '100%',
  padding: '180px 64px 16px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    padding: '96px 24px 16px',
    gap: '28px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '80px 16px 12px'
  }
}));

const VideoCard = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: '16px',
  background: '#1e293b', // slate-800
  boxShadow: '0 0 40px rgba(34, 211, 238, 0.2)',
  border: '2px solid transparent',
  backgroundImage: 'linear-gradient(#1e293b, #1e293b), linear-gradient(90deg, #22d3ee, #a5b4fc)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  padding: '12px'
}));

const VideoFrame = styled('video')(({ theme }) => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  borderRadius: '12px',
  outline: 'none',
  border: 'none',
  objectFit: 'contain',
  backgroundColor: '#000'
}));

// Inline AI chip for headline
const AIChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  padding: '1px 10px',
  borderRadius: '6px',
  color: '#0f172a',
  fontWeight: 700,
  fontSize: '0.68em',
  verticalAlign: 'middle',
  margin: '0 8px'
}));

// Search dropdown for business results
const SearchDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '8px',
  backgroundColor: '#1e293b',
  borderRadius: '16px',
  border: '1px solid #334155',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
  maxHeight: '350px',
  overflowY: 'auto',
  zIndex: 9999,
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#1e293b'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#475569',
    borderRadius: '3px'
  }
}));

const BusinessItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '14px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #334155',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#334155'
  },
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const BusinessIcon = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: '#0B91D6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

const BusinessInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0
}));

const BusinessName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  color: '#ffffff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const BusinessAddress = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  color: '#94a3b8',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginTop: '2px'
}));

const BusinessRating = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '4px'
}));

const CompleteHeroWithHeader = ({ onAboutClick }) => {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Search for businesses
  const searchBusinesses = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const url = `${API_BASE_URL}/gmb/search-business?query=${encodeURIComponent(query)}`;
      console.log('[Business Search] Calling API:', url);

      const response = await fetch(url);
      const data = await response.json();

      console.log('[Business Search] API Response:', data);

      // Check for businesses in response (code: 200 means success)
      const businesses = data.data?.businesses || [];
      if (businesses.length > 0) {
        console.log('[Business Search] Found businesses:', businesses.length);
        setSearchResults(businesses);
        setShowDropdown(true);
      } else {
        console.log('[Business Search] No businesses found');
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('[Business Search] Error:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setBusinessName(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchBusinesses(value);
    }, 300);
  };

  // Handle business selection
  const handleBusinessSelect = (business) => {
    setShowDropdown(false);
    setBusinessName(business.name);
    // Navigate to login with selected business info
    navigate('/login', {
      state: {
        selectedBusiness: {
          placeId: business.placeId,
          name: business.name,
          address: business.address
        }
      }
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleScanClick = () => {
    logger.log('Check Now button clicked, navigating to login page');
    navigate('/login');
  };

  const handleAboutClick = () => {
    if (onAboutClick) {
      onAboutClick();
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const gridPins = [
    { rank: '14', color: '#FBBC05' },
    { rank: '20+', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#EF232A' },
    { rank: '14', color: '#A0A0AA' },
    { rank: '20+', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#EF232A' },
    { rank: '14', color: '#A0A0AA' },
    { rank: '20+', color: '#34A853' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#EF232A' },
    { rank: '14', color: '#A0A0AA' },
    { rank: '20+', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#A0A0AA' },
    { rank: '18', color: '#EF232A' },
    { rank: '14', color: '#A0A0AA' },
    { rank: '20+', color: '#34A853' },
    { rank: '20+', color: '#A0A0AA' },
    { rank: '20+', color: '#A0A0AA' },
    { rank: '18', color: '#EF232A' }
  ];

  return (
    <HeroContainer>
      {/* Header integrated inside hero */}
      <HeaderBar>
        <LogoImage onClick={handleLogoClick} />

        <NavLinks>
          <NavLink onClick={() => scrollToSection('features')}>Features</NavLink>
          <NavLink onClick={handleAboutClick}>About Us</NavLink>
          <NavLink onClick={() => scrollToSection('pricing')}>Pricing</NavLink>
        </NavLinks>

        <LoginButton onClick={handleLoginClick}>
          Login
        </LoginButton>
      </HeaderBar>

      {/* Two-column layout: video left, content right */}
      <HeroLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <TopBadge>
            <Typography sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '16px' },
              color: '#0f172a',
              lineHeight: { xs: '18px', sm: '20px', md: '22px', lg: '24px' }
            }}>
              Stop outsourcing your growth – Visibeen makes you the expert
            </Typography>
          </TopBadge>

          <MainHeadline>
            From Insights To Execution<br />
            <AIChip>AI</AIChip> Makes It Simple
          </MainHeadline>

          <Description>
            Visibeen is India's first DIY Local SEO platform that shows your Google visibility score, benchmarks you against competitors, and gives you simple AI tasks to improve rankings and sales.
          </Description>

          <Box ref={dropdownRef} sx={{ position: 'relative', width: '100%', maxWidth: '520px', marginTop: '24px', zIndex: 9999 }}>
            <SearchBar>
              <SearchInput
                type="text"
                placeholder="Search your business name"
                value={businessName}
                onChange={handleSearchChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleScanClick();
                  }
                }}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                data-path="main_input"
              />
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#94a3b8' }} />
                </Box>
              ) : (
                <ScanButton
                  onClick={handleScanClick}
                  className="index-header__cta"
                  data-check-sre=""
                  data-path="main_input"
                >
                  <Typography sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    color: '#ffffff',
                    whiteSpace: 'nowrap'
                  }}>
                    Get insights
                  </Typography>
                </ScanButton>
              )}
            </SearchBar>

            {showDropdown && searchResults.length > 0 && (
              <SearchDropdown>
                {searchResults.map((business, index) => (
                  <BusinessItem
                    key={business.placeId || index}
                    onClick={() => handleBusinessSelect(business)}
                  >
                    <BusinessIcon>
                      <MapPin size={20} color="#ffffff" />
                    </BusinessIcon>
                    <BusinessInfo>
                      <BusinessName>{business.name}</BusinessName>
                      <BusinessAddress>{business.address}</BusinessAddress>
                      {business.rating > 0 && (
                        <BusinessRating>
                          <Star size={12} fill="#FCD34D" color="#FCD34D" />
                          <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                            {Number(typeof business.rating === 'object' ? business.rating?.value : business.rating).toFixed(1)} ({business.userRatingCount || 0} reviews)
                          </Typography>
                        </BusinessRating>
                      )}
                    </BusinessInfo>
                  </BusinessItem>
                ))}
              </SearchDropdown>
            )}
          </Box>
        </Box>
        <VideoCard>
          <VideoFrame autoPlay loop muted playsInline>
            <source src="/images/dashboard-animation.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoFrame>
        </VideoCard>
      </HeroLayout>
    </HeroContainer>
  );
};

export default CompleteHeroWithHeader;
